import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Calendar, MessageCircle, Phone, ArrowRight, User, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PropertySidebar({ agency, propertyId, onEnquire }) {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: 'I am interested in this property. Please contact me.'
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // Hardcoded for demo - ideally from auth context
    const isLoggedIn = true;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Call parent handler
        if (onEnquire) {
            onEnquire(formData);
        }

        setSuccess(true);
        setLoading(false);
        toast.success("Enquiry sent successfully!");
    };

    if (success) {
        return (
            <Card className="shadow-lg border-green-100 bg-green-50/50 sticky top-24">
                <CardContent className="p-8 text-center space-y-4">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-4">
                        <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-green-800">Request Sent!</h3>
                    <p className="text-green-700">
                        The agency has been notified. They will contact you shortly.
                    </p>
                    <Button
                        variant="outline"
                        className="w-full mt-4 bg-white hover:bg-green-50"
                        onClick={() => setSuccess(false)}
                    >
                        Send another enquiry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="sticky top-24 shadow-lg border-navy-100 overflow-hidden">
            <CardHeader className="bg-navy-50/50 pb-4">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-lg">
                        {agency?.company_name?.charAt(0) || 'A'}
                    </div>
                    <div>
                        <p className="text-xs text-navy-500 uppercase font-semibold tracking-wider">Listing Agent</p>
                        <h3 className="font-bold text-navy-900 leading-tight">{agency?.company_name || 'EstateCloud Partner'}</h3>
                    </div>
                </div>
                <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" className="flex-1 gap-2 bg-white" onClick={() => window.open(`tel:${agency?.phone_number}`)}>
                        <Phone className="w-4 h-4" /> Call
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 gap-2 bg-white" onClick={() => window.open(`https://wa.me/${agency?.whatsapp_number || agency?.phone_number}`)}>
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </Button>
                </div>
            </CardHeader>

            <CardContent className="p-6 space-y-4">
                <div className="space-y-1">
                    <h4 className="font-semibold text-navy-900">Book a Site Visit</h4>
                    <p className="text-sm text-navy-500">Fill the form below to request a tour.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-xs uppercase text-navy-500">Full Name</Label>
                        <Input
                            id="name"
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            className="bg-navy-50/30"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="phone" className="text-xs uppercase text-navy-500">Phone Number</Label>
                        <Input
                            id="phone"
                            placeholder="+91..."
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            required
                            className="bg-navy-50/30"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="message" className="text-xs uppercase text-navy-500">Message</Label>
                        <Textarea
                            id="message"
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            rows={3}
                            className="bg-navy-50/30 resize-none text-sm"
                        />
                    </div>

                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-200" disabled={loading}>
                        {loading ? 'Sending...' : 'Send Request'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>

                    <p className="text-xs text-center text-navy-400">
                        By sending, you agree to our Terms of Service.
                    </p>
                </form>
            </CardContent>
        </Card>
    );
}
